import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { COMMERCE } from '@/config/site';
import { readStorage, writeStorage } from '@/lib/storage';

import { FavoritesContext, type FavoritesValue } from './favorites.context';

function loadIds(): string[] {
  const stored = readStorage<unknown>(COMMERCE.favoritesStorageKey, []);
  if (!Array.isArray(stored)) return [];
  return stored.filter((id): id is string => typeof id === 'string');
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [ids, setIds] = useState<string[]>(loadIds);

  useEffect(() => {
    writeStorage(COMMERCE.favoritesStorageKey, ids);
  }, [ids]);

  // Mantém as abas em sincronia: sem isto, a última aba a favoritar algo
  // sobrescreve a lista da outra.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== COMMERCE.favoritesStorageKey || event.newValue === null) return;

      try {
        const parsed: unknown = JSON.parse(event.newValue);
        if (Array.isArray(parsed)) {
          setIds(parsed.filter((id): id is string => typeof id === 'string'));
        }
      } catch {
        // Valor ilegível de outra aba — mantém a lista atual.
      }
    }

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback((productId: string) => {
    setIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId],
    );
  }, []);

  const clear = useCallback(() => setIds([]), []);

  const value = useMemo<FavoritesValue>(
    () => ({
      ids,
      isFavorite: (productId) => ids.includes(productId),
      toggle,
      clear,
      count: ids.length,
    }),
    [ids, toggle, clear],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}
